/**
 * WHICH STEP TO SHOW — the pure half of GatedStepper (plan S6, R-S6.3).
 *
 * The server decides every step's state; this only answers "the guest asked
 * for step X (a deep link, a reload, a back button): may they see it, and if
 * not, where do they land instead?". It never unlocks anything — a locked or
 * not-required step is never returned for a request, and the server still
 * refuses a write to a locked step with 409 STEP_LOCKED.
 *
 * Order of answers:
 *   1. the requested step, when its state is 'current' or 'done' (review);
 *   2. otherwise the 'current' step;
 *   3. if nothing is current, the LAST 'done' step;
 *   4. if nothing is done either, the first COUNTED step (not 'not_required');
 *   5. if no step is counted at all, '' (the caller has nothing to render).
 *
 * The table of cases is src/utils/stays-checkin-steps.cases.json, kept equal
 * to the server's copy by scripts/stays/check-fe-copy.js.
 */

export type StepState = 'done' | 'current' | 'locked' | 'not_required';

export interface StepDef {
  id: string;
  label: string;
  state: StepState;
  /** Shown as visible text beside a locked step, e.g. "Finish Identity first". */
  lockedReason?: string;
}

/** Only the two fields resolution reads, so plain case rows fit too. */
export type StepShape = Pick<StepDef, 'id' | 'state'>;

const reviewable = (state: StepState): boolean =>
  state === 'current' || state === 'done';

/** The steps that are rendered and counted: everything but 'not_required'. */
export const countedSteps = <S extends StepShape>(
  steps: ReadonlyArray<S>
): S[] => steps.filter(step => step.state !== 'not_required');

export function resolveStep(
  requestedId: string | undefined,
  steps: ReadonlyArray<StepShape>
): string {
  if (requestedId !== undefined) {
    const requested = steps.find(step => step.id === requestedId);
    if (requested && reviewable(requested.state)) return requested.id;
  }

  const current = steps.find(step => step.state === 'current');
  if (current) return current.id;

  const done = steps.filter(step => step.state === 'done');
  if (done.length > 0) return done[done.length - 1].id;

  const counted = countedSteps(steps);

  return counted.length > 0 ? counted[0].id : '';
}
